import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["received", "under_review", "approved", "rejected", "attended", "not_attended", "completed", "offered", "registered", "cancelled"];

const getStatusColor = (status) => {
  if (["approved", "registered", "attended"].includes(status)) return "bg-success/10 text-success border-success/20";
  if (["rejected", "not_attended", "cancelled"].includes(status)) return "bg-destructive/10 text-destructive border-destructive/20";
  if (["received", "under_review"].includes(status)) return "bg-warning/10 text-warning border-warning/20";
  return "bg-muted";
};

export default function SchoolApplicationsPage() {
  const [items, setItems] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const load = () => api.get(`${V1}/scholarship-applications/mine`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`${V1}/scholarship-applications/${id}`, { status });
      toast.success("Durum güncellendi");
      load();
    } catch { toast.error("Güncellenemedi"); }
  };

  return (
    <div className="space-y-6" data-testid="school-applications-page">
      <div>
        <div className="overline text-accent">Başvurular</div>
        <h1 className="font-display text-3xl font-bold">Gelen Başvurular</h1>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Başvuru No</th>
              <th className="text-left p-3">Öğrenci</th>
              <th className="text-left p-3">Sınav</th>
              <th className="text-left p-3">Sınıf</th>
              <th className="text-left p-3">Durum</th>
              <th className="text-right p-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Henüz başvuru yok.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-border" data-testid={`school-app-${a.id}`}>
                <td className="p-3 font-mono text-xs">{a.application_no}</td>
                <td className="p-3">{a.child?.name} {a.child?.surname}</td>
                <td className="p-3">{a.exam?.title}</td>
                <td className="p-3">{a.child?.grade_level}</td>
                <td className="p-3">
                  <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                    <SelectTrigger className={`h-8 w-40 text-xs font-medium uppercase border ${getStatusColor(a.status)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setSelectedApp(a)}>
                    <Eye className="h-4 w-4 mr-2" /> Detay
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selectedApp} onOpenChange={(open) => !open && setSelectedApp(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Başvuru Detayı</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4 text-sm mt-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="text-muted-foreground">Başvuru No:</div>
                <div className="font-mono">{selectedApp.application_no}</div>
                
                <div className="text-muted-foreground">Öğrenci Adı:</div>
                <div>{selectedApp.child?.name} {selectedApp.child?.surname}</div>
                
                <div className="text-muted-foreground">Sınıf:</div>
                <div>{selectedApp.child?.grade_level}</div>
                
                <div className="text-muted-foreground">Sınav:</div>
                <div>{selectedApp.exam?.title}</div>
                
                <div className="text-muted-foreground">Veli Adı:</div>
                <div>{selectedApp.parent?.name}</div>
                
                <div className="text-muted-foreground">İletişim:</div>
                <div>{selectedApp.parent?.email} {selectedApp.parent?.phone && ` / ${selectedApp.parent?.phone}`}</div>
                
                <div className="text-muted-foreground">Tarih:</div>
                <div>{new Date(selectedApp.created_at).toLocaleString("tr-TR")}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
